import { useEffect, useState } from 'react'
import classes from './styles.module.scss'
import generalClasses from './styles.module.scss'
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IInfrastructureProject, IInfrastructureProjectObject, InfrastructureProjectType } from '../../../types/project.types';
import { updateInfrastructureProject } from '../../../requests/supervisor.request';
import { isEmpty, t } from '../../../helpers/utils';
import Field from '../../../components/field/field.component';
import AdditionalInfo from '../components/additional-info.component';
import ImageGrid from '../components/image-grid.component';
import FooterComponent from '../components/footer.component';

const keys: (keyof IInfrastructureProject)[] = [
    'nameRu', 'nameKz', 'realizationYear', 'projectPowerRu', 'projectPowerKz', 'investmentFund', 'workPlaceNumber',
    'shortDescriptionRu', 'shortDescriptionKz', 'expectedEffectRu', 'expectedEffectKz'
]

interface IProps {
    data: IInfrastructureProjectObject
    loadData: () => void;
}

const baseClass = 'investment-project-tab';

const InvestmentProjectTab = ({ data, loadData }: IProps) => {
    const { kato } = useParams<{ link: string, kato: string }>();
    const { i18n: { language } } = useTranslation();
    const [formData, setFormData] = useState<IInfrastructureProjectObject>(data);
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [isAddingInfo, setIsAddingInfo] = useState(false);
    const [newInfo, setNewInfo] = useState<{ labelRu: string; labelKz: string; valueRu: string; valueKz: string }>({ labelRu: '', labelKz: '', valueRu: '', valueKz: '' });

    const [isAddingText, setIsAddingText] = useState(false);
    const [newText, setNewText] = useState({
        textRu: '',
        textKz: ''
    });

    const handleInputChange = (field: string, value: string | number) => {
        if (field === 'realizationYear' && value.toString().length > 4) return;
        setFormData(prev => ({
            ...prev,
            infrastructureEntity: {
                ...prev.infrastructureEntity,
                [field]: value
            }
        }));
    };

    const handleSave = () => {
        if (validateFields()) {
            const body = {
                ...formData.infrastructureEntity,
            }

            updateInfrastructureProject(InfrastructureProjectType.SNP_INVESTMENT, body).then(() => {
                loadData();
                toast.success(t(`admin.success`, language));
            });
        }
    };

    const validateFields = () => {
        setErrors({});
        const newErrors: Record<string, boolean> = {};
        const { infrastructureEntity } = formData;

        keys.forEach(key => {
            if (isEmpty(infrastructureEntity[key])) {
                newErrors[key] = true;
            }
        });

        setErrors(newErrors);

        return Object.values(newErrors).every(error => !error);
    }

    useEffect(() => {
        if (!data) return;
        setFormData(data);
    }, [data]);

    const renderFields = (lang: 'ru' | 'kz') => {
        const suffix = lang === 'ru' ? 'Ru' : 'Kz';
        const { infrastructureEntity } = formData;

        return <div className={generalClasses[`fields-container`]}>
            <Field
                key={`name${suffix}`}
                label={t('projects.infrastructure-projects.name', lang)}
                value={infrastructureEntity?.[`name${suffix}`] || ''}
                onChange={(value) => {
                    handleInputChange(`name${suffix}`, value);
                }}
                type="text"
                required
                error={errors[`name${suffix}`]}
            />
            <div className={`${generalClasses[`field-block`]}`}>
                {['realizationYear', 'projectPower', 'investmentFund', 'workPlaceNumber'].map(field => {
                    const valueKey = field === 'projectPower' ? `${field}${suffix}` : field;
                    const val = infrastructureEntity?.[valueKey as keyof typeof infrastructureEntity];
                    const value = val === '' ? '' : val as string | number;
                    return (
                        <Field
                            key={field}
                            label={t(`projects.SNP_INVESTMENT.${field}`, lang)}
                            value={value ?? ''}
                            onChange={(newValue) => {
                                const val = newValue === '' ? '' : newValue;
                                handleInputChange(valueKey, val);
                            }}
                            type={field !== 'projectPower' ? 'number' : 'text'}
                            required
                            error={errors[valueKey]}
                        />
                    )
                })}
            </div>
            <div className={`${generalClasses[`field-block`]}`}>
                {['shortDescription', 'expectedEffect'].map(field => {
                    const valueKey = `${field}${suffix}`;
                    const val = infrastructureEntity?.[valueKey as keyof typeof infrastructureEntity];
                    const value = val === '' ? '' : val as string | number;
                    return (
                        <Field
                            key={field}
                            label={t(`projects.SNP_INVESTMENT.${field}`, lang)}
                            value={value ?? ''}
                            onChange={(newValue) => {
                                const val = newValue === '' ? '' : newValue;
                                handleInputChange(valueKey, val);
                            }}
                            type={field !== 'projectPower' ? 'number' : 'text'}
                            required
                            error={errors[valueKey]}
                            as={field === 'shortDescription' ? 'textarea' : 'input'}
                        />
                    )
                })}
            </div>
            <AdditionalInfo
                data={formData}
                setData={setFormData}
                lang={lang}
                newText={newText}
                setNewText={setNewText}
                newInfo={newInfo}
                setNewInfo={setNewInfo}
                isAddingText={isAddingText}
                setIsAddingText={setIsAddingText}
                isAddingInfo={isAddingInfo}
                setIsAddingInfo={setIsAddingInfo}
                parentErrors={errors}
            />
        </div>
    }
    if (!data || !formData) return null;
    return (
        kato && <div className={classes[baseClass]}>
            <div className={generalClasses['grid']}>
                {renderFields(language === 'ru' ? 'ru' : 'kz')}
                {renderFields(language === 'ru' ? 'kz' : 'ru')}
            </div>
            <ImageGrid
                kato={+kato}
                imageData={formData.images}
                type={'infrastructure-projects'}
                objectId={formData.infrastructureEntity.id}
                setFormData={setFormData}
            />
            <FooterComponent
                onSave={handleSave}
                validateFields={validateFields}
            />
        </div>
    )
}

export default InvestmentProjectTab